<script context="module" lang="ts">
    import type { ContextualMenuItem } from '../ProductContextMenu/types';
    export interface SortConfig {
        items: ContextualMenuItem[];
        sortBy: string;
        sortOrderAction?: { url: string };
        isReversible: boolean;
    }
</script>

<script lang="ts">
    import SortIcon from '@amp/web-app-components/assets/icons/sort.svg';
    import ChevronIcon from '@amp/web-app-components/assets/icons/chevron-down.svg';
    import { createEventDispatcher } from 'svelte';

    const checkIcon =
        '<svg viewBox="0 0 16 16"><path d="M6.9 12.9c.3 0 .5-.1.7-.4l5.3-8.4c.1-.1.1-.3.1-.4 0-.4-.2-.6-.6-.6-.3 0-.4.1-.6.3l-5 8-2.6-3.5c-.2-.2-.3-.3-.6-.3s-.6.2-.6.6c0 .2.1.3.2.5l3 3.9c.2.2.4.3.7.3z"/></svg>';

    /**
     * translate function provided by the parent app.
     */
    export let translateFn: (
        str: string,
        values?: Record<string, string | number>,
    ) => string;
    export let config: SortConfig;
    export let ariaLabel: string;

    const dispatch = createEventDispatcher();
    const handleItemAction = (value) => {
        if (config.sortBy !== value) {
            dispatch('sortByChange', value);
        }
    };

    $: currentOrder = config.sortBy?.slice(0, 1) === '-' ? '-' : '';
    $: currentField =
        currentOrder === '' ? config.sortBy : config.sortBy?.slice(1);
    $: items = [
        ...(config.items?.map((item) => ({
            ...item,
            action: () =>
                handleItemAction(item.action ?? `${currentOrder}${item.id}`),
            ...(currentField === item.id ? { icon: checkIcon } : {}),
        })) ?? []),
        {
            items: [
                {
                    label: translateFn('FUSE.Sorts.Ascending'),
                    ...(!config.isReversible
                        ? {
                              styles: { cursor: 'not-allowed', opacity: 0.35 },
                          }
                        : {
                              action: () =>
                                  handleItemAction(
                                      config.sortOrderAction ?? currentField,
                                  ),
                          }),
                    ...(currentField && currentOrder === ''
                        ? { icon: checkIcon }
                        : {}),
                },
                {
                    label: translateFn('FUSE.Sorts.Descending'),
                    ...(!config.isReversible
                        ? {
                              styles: { cursor: 'not-allowed', opacity: 0.35 },
                          }
                        : {
                              action: () =>
                                  handleItemAction(
                                      config.sortOrderAction ??
                                          `-${currentField}`,
                                  ),
                          }),
                    ...(currentField && currentOrder === '-'
                        ? { icon: checkIcon }
                        : {}),
                },
            ],
        },
    ];
</script>

{#if config.items?.length > 0}
    <amp-contextual-menu-button config={{ items }}>
        <span
            class="sort-button"
            data-testid="sort-menu-trigger"
            aria-label={ariaLabel}
            slot="trigger-content"
        >
            <SortIcon data-testid="sort-menu-icon" aria-hidden="true" />
            <ChevronIcon class="chevron" aria-hidden="true" />
        </span>
    </amp-contextual-menu-button>
{/if}

<style lang="scss">
    .sort-button {
        $sort-menu-button-size: 28px;
        $sort-menu-chevron-size: 5px;
        min-width: $sort-menu-button-size; // Using min-width, as button can be wider with chevron
        height: $sort-menu-button-size;
        display: flex;
        justify-content: center;
        align-items: center;

        :global(svg) {
            fill: var(--systemSecondary);
        }

        :global(.chevron) {
            height: $sort-menu-chevron-size;
            padding-inline-start: 10px;

            // Hide chevron in mobile view
            @media (--range-sidebar-hidden-down) {
                display: none;
            }
        }
    }
</style>
